<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="beneficiary-detail-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.push('/beneficiaries')" class="back-button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <div v-if="isLoading" class="loading-state">
        {{ $t('common.loading') }}
      </div>

      <template v-else-if="beneficiary">
        <!-- Beneficiary Header -->
        <div class="beneficiary-header">
          <div class="beneficiary-info">
            <h1 class="beneficiary-name">
              <span v-if="beneficiary.beneficiary_number" class="beneficiary-number">{{ beneficiary.beneficiary_number }}</span>
              {{ beneficiary.name }}
            </h1>
            <div class="beneficiary-meta">
              <IntercomBadge v-if="beneficiary.beneficiary_type" :variant="getTypeVariant(beneficiary.beneficiary_type)">
                {{ $t(`beneficiary.types.${beneficiary.beneficiary_type}`) }}
              </IntercomBadge>
              <IntercomBadge :variant="beneficiary.is_active ? 'success' : 'default'">
                {{ beneficiary.is_active ? $t('common.active') : $t('common.inactive') }}
              </IntercomBadge>
              <span v-if="beneficiary.phone" class="meta-item">{{ beneficiary.phone }}</span>
              <span v-if="beneficiary.email" class="meta-item">{{ beneficiary.email }}</span>
            </div>
          </div>
          <div class="beneficiary-actions">
            <IntercomButton variant="secondary" @click="showEditModal = true">
              {{ $t('common.edit') }}
            </IntercomButton>
            <IntercomButton variant="primary" @click="showEnrollModal = true">
              {{ $t('enrollment.enroll') }}
            </IntercomButton>
          </div>
        </div>

        <!-- Details Card -->
        <IntercomCard v-if="beneficiary.address || beneficiary.date_of_birth || beneficiary.gender || beneficiary.category || beneficiary.notes" class="details-card">
          <h3>{{ $t('common.details') }}</h3>
          <div v-if="beneficiary.gender" class="detail-row">
            <span class="detail-label">{{ $t('beneficiary.gender') }}</span>
            <span class="detail-value">{{ $t(`beneficiary.genders.${beneficiary.gender}`) }}</span>
          </div>
          <div v-if="beneficiary.date_of_birth" class="detail-row">
            <span class="detail-label">{{ $t('beneficiary.dateOfBirth') }}</span>
            <span class="detail-value">{{ formatDate(beneficiary.date_of_birth) }}</span>
          </div>
          <div v-if="beneficiary.category" class="detail-row">
            <span class="detail-label">{{ $t('beneficiary.category') }}</span>
            <IntercomBadge :variant="getCategoryVariant(beneficiary.category)">
              {{ $t(`beneficiary.categories.${beneficiary.category}`) }}
            </IntercomBadge>
          </div>
          <div v-if="beneficiary.address" class="detail-row">
            <span class="detail-label">{{ $t('beneficiary.address') }}</span>
            <span class="detail-value">{{ beneficiary.address }}</span>
          </div>
          <div v-if="beneficiary.notes" class="detail-row">
            <span class="detail-label">{{ $t('beneficiary.notes') }}</span>
            <span class="detail-value">{{ beneficiary.notes }}</span>
          </div>
        </IntercomCard>

        <!-- KYC Documents Card -->
        <IntercomCard v-if="beneficiary.kyc_documents && beneficiary.kyc_documents.length > 0" class="details-card">
          <h3>{{ $t('beneficiary.kycDocuments') }}</h3>
          <div v-for="(doc, i) in beneficiary.kyc_documents" :key="i" class="detail-row">
            <span class="detail-label">{{ $t(`beneficiary.idProofTypes.${doc.type}`) }}</span>
            <span class="detail-value kyc-number">{{ maskDocNumber(doc.type, doc.number) }}</span>
          </div>
        </IntercomCard>

        <!-- Enrollments Section -->
        <IntercomCard class="enrollments-card">
          <div class="section-header">
            <h3>{{ $t('enrollment.title') }}</h3>
          </div>
          <IntercomTable
            :columns="enrollmentColumns"
            :data="enrollments"
            :loading="loadingEnrollments"
            hoverable
          >
            <template #cell-status="{ value }">
              <IntercomBadge :variant="getEnrollmentStatusVariant(value)">
                {{ $t(`enrollment.statuses.${value}`) }}
              </IntercomBadge>
            </template>
            <template #cell-enrolled_date="{ value }">
              {{ formatDate(value) }}
            </template>
          </IntercomTable>
          <p v-if="!loadingEnrollments && enrollments.length === 0" class="empty-text">
            {{ $t('enrollment.noEnrollments') }}
          </p>
        </IntercomCard>
      </template>

      <!-- Edit Modal -->
      <IntercomModal v-model:show="showEditModal" :title="$t('beneficiary.editBeneficiary')">
        <BeneficiaryForm
          :beneficiary="beneficiary"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="showEditModal = false"
        />
      </IntercomModal>

      <!-- Enroll Modal -->
      <IntercomModal v-model:show="showEnrollModal" :title="$t('enrollment.enroll')">
        <EnrollmentForm
          :programs="programs"
          :loading="isEnrolling"
          @submit="handleEnroll"
          @cancel="showEnrollModal = false"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import BeneficiaryForm from '../components/BeneficiaryForm.vue'
import EnrollmentForm from '../components/EnrollmentForm.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getBeneficiary, updateBeneficiary, listEnrollments, createEnrollment, listPrograms } = useSeva()
const { navSections } = useNavigation()

// State
const beneficiary = ref(null)
const enrollments = ref([])
const programs = ref([])
const isLoading = ref(false)
const loadingEnrollments = ref(false)
const isSaving = ref(false)
const isEnrolling = ref(false)
const showEditModal = ref(false)
const showEnrollModal = ref(false)

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const enrollmentColumns = [
  { key: 'program_name', title: t('enrollment.program'), width: '200px' },
  { key: 'enrolled_date', title: t('enrollment.enrolledDate'), width: '130px' },
  { key: 'status', title: t('enrollment.status'), width: '120px' },
]

const getTypeVariant = (type) => {
  switch (type) {
    case 'individual': return 'info'
    case 'household': return 'success'
    case 'group': return 'warning'
    default: return 'default'
  }
}

const getCategoryVariant = (category) => {
  switch (category) {
    case 'bpl': return 'danger'
    case 'senior_citizen': return 'warning'
    case 'disabled': return 'info'
    case 'student': return 'success'
    case 'general': return 'default'
    default: return 'default'
  }
}

const maskDocNumber = (type, number) => {
  if (!number) return '-'
  if (type === 'aadhaar' && number.length === 12) {
    return 'XXXX XXXX ' + number.slice(-4)
  }
  return number
}

const getEnrollmentStatusVariant = (status) => {
  switch (status) {
    case 'active': return 'success'
    case 'completed': return 'info'
    case 'withdrawn': return 'default'
    case 'waitlisted': return 'warning'
    default: return 'default'
  }
}

const fetchBeneficiaryData = async () => {
  const beneficiaryId = route.params.id
  isLoading.value = true
  try {
    const response = await getBeneficiary(beneficiaryId)
    beneficiary.value = response?.beneficiary || response
  } catch (error) {
    console.error('Failed to fetch beneficiary:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const fetchEnrollments = async () => {
  const beneficiaryId = route.params.id
  loadingEnrollments.value = true
  try {
    const response = await listEnrollments({ beneficiary_id: beneficiaryId })
    enrollments.value = response?.data || []
  } catch (error) {
    console.error('Failed to fetch enrollments:', error)
  } finally {
    loadingEnrollments.value = false
  }
}

const fetchPrograms = async () => {
  try {
    const response = await listPrograms()
    programs.value = response?.programs || []
  } catch (error) {
    console.error('Failed to fetch programs:', error)
  }
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    const data = { ...formData }
    if (data.phone && data.phone.startsWith('+91')) data.phone = data.phone.slice(3)
    if (!data.phone) data.phone = null

    await updateBeneficiary(beneficiary.value.id, data)
    toast.success(t('success.beneficiaryUpdated'))
    showEditModal.value = false
    fetchBeneficiaryData()
  } catch (error) {
    console.error('Failed to save beneficiary:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const handleEnroll = async (formData) => {
  isEnrolling.value = true
  try {
    await createEnrollment({
      ...formData,
      beneficiary_id: route.params.id,
    })
    toast.success(t('success.enrollmentCreated'))
    showEnrollModal.value = false
    fetchEnrollments()
  } catch (error) {
    console.error('Failed to create enrollment:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isEnrolling.value = false
  }
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchBeneficiaryData()
  fetchEnrollments()
  fetchPrograms()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.beneficiary-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-button {
  margin-bottom: var(--space-4);
}

.loading-state {
  text-align: center;
  padding: var(--space-12);
  color: var(--color-gray-500);
}

.beneficiary-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-6);
}

.beneficiary-name {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-2) 0;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.beneficiary-number {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  background: var(--color-gray-100);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
}

.beneficiary-meta {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  flex-wrap: wrap;
}

.meta-item {
  padding-left: var(--space-3);
  border-left: 1px solid var(--color-gray-300);
}

.beneficiary-actions {
  display: flex;
  gap: var(--space-3);
}

.details-card {
  margin-bottom: var(--space-6);
}

.details-card h3 {
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-lg);
}

.detail-row {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  width: 130px;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  color: var(--color-charcoal);
}

.enrollments-card {
  margin-bottom: var(--space-6);
}

.section-header {
  margin-bottom: var(--space-4);
}

.section-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .beneficiary-detail-view {
    padding: var(--space-4);
  }

  .beneficiary-header {
    flex-direction: column;
    gap: var(--space-4);
  }
}
</style>
